import { formatOptionalBenefitDisplayValue } from "@/lib/benefit-labels"

export type OptionalBenefitCatalogGroup = {
  benefitType: string
  selectedId?: string | null
  options: Array<{
    id: string
    limitValue?: string | number | null
    copayPct?: number | null
    optionLabel?: string
    isDefault?: boolean
  }>
}

export type OptionalBenefitRow = {
  id?: string
  benefitType?: string
  limitValue?: string | number | null
  copayPct?: number | null
  optionLabel?: string
  isDefault?: boolean
}

function normalizeBenefitKey(key: string): string {
  return key
    .replace(/([A-Z])/g, "_$1")
    .toLowerCase()
    .replace(/^_/, "")
}

function catalogMatchesBenefitKey(
  catalogBenefitType: string,
  benefitKey: string
): boolean {
  return (
    catalogBenefitType === benefitKey ||
    normalizeBenefitKey(catalogBenefitType) ===
      normalizeBenefitKey(benefitKey)
  )
}

export function buildOptionalBenefitOptionMap(
  catalog?: OptionalBenefitCatalogGroup[]
): Map<string, OptionalBenefitCatalogGroup["options"][number]> {
  const map = new Map<string, OptionalBenefitCatalogGroup["options"][number]>()
  for (const group of catalog ?? []) {
    for (const option of group.options ?? []) {
      map.set(option.id, option)
    }
  }
  return map
}

export function resolveGroupOptionalBenefitRow(
  selected: unknown,
  catalog?: OptionalBenefitCatalogGroup[],
  benefitKey?: string,
  optionById?: Map<string, OptionalBenefitCatalogGroup["options"][number]>
): OptionalBenefitRow | null {
  if (selected == null || selected === false) return null

  if (typeof selected === "object") {
    const row = selected as OptionalBenefitRow
    if (
      row.limitValue != null ||
      row.optionLabel != null ||
      row.benefitType != null ||
      row.copayPct != null
    ) {
      return row
    }
  }

  if (typeof selected !== "string") return null

  const byId = (optionById ?? buildOptionalBenefitOptionMap(catalog)).get(
    selected
  )
  if (byId) return byId

  if (!catalog || !benefitKey) return null

  const group = catalog.find((entry) =>
    catalogMatchesBenefitKey(entry.benefitType, benefitKey)
  )
  if (!group) return null

  return (
    group.options.find((option) => option.id === selected) ??
    group.options.find((option) => option.id === group.selectedId) ??
    group.options.find((option) => option.isDefault) ??
    group.options[0] ??
    null
  )
}

export function formatGroupOptionalBenefitDisplay(
  row: OptionalBenefitRow | null,
  currency = "AED"
): string {
  return formatOptionalBenefitDisplayValue(row, currency)
}
